import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Badge, Button, Col, Form, Row, Spinner, Table } from "react-bootstrap";
import { ExclamationTriangleFill, PencilSquare, PlusLg } from "react-bootstrap-icons";
import { apiGet, TEST_TYPES } from "./contentApi";
import { QuestionFormModal, TestFormModal } from "./TestFormModals";

const OPTION_KEYS = [
    { letter: "a", field: "optionA", label: "A" },
    { letter: "b", field: "optionB", label: "B" },
    { letter: "c", field: "optionC", label: "C" },
];

// Correct answers are stored as the option letter ("a", "b" or "c").
const getQuestionIssue = (question) => {
    const letter = (question.correctOption || "").trim().toLowerCase();
    const option = OPTION_KEYS.find((o) => o.letter === letter);

    if (!question.question?.trim()) return "Question text is empty";
    if (!option) return `Correct answer "${question.correctOption ?? ""}" is not A, B or C`;
    if (!question[option.field]?.trim()) return `Correct answer ${option.label} is an empty option`;
    return null;
};

const TestsTab = ({ levels }) => {
    const [levelId, setLevelId] = useState("");
    const [type, setType] = useState(TEST_TYPES[0].value);
    const [tests, setTests] = useState([]);
    const [testId, setTestId] = useState("");
    const [questions, setQuestions] = useState([]);
    const [showOnlyIssues, setShowOnlyIssues] = useState(false);
    const [loadingTests, setLoadingTests] = useState(false);
    const [loadingQuestions, setLoadingQuestions] = useState(false);
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");
    const [testForm, setTestForm] = useState({ show: false, test: null });
    const [questionForm, setQuestionForm] = useState({ show: false, question: null });
    const [testsReloadKey, setTestsReloadKey] = useState(0);
    const [questionsReloadKey, setQuestionsReloadKey] = useState(0);
    // Test to select after the list reloads (e.g. a newly created one).
    const preferredTestIdRef = useRef(null);

    const testFormDefaults = useMemo(() => ({ levelId, type }), [levelId, type]);

    const showNotice = (message) => {
        setNotice(message);
        setTimeout(() => setNotice(""), 2500);
    };

    const handleTestSaved = (saved) => {
        setTestForm({ show: false, test: null });
        preferredTestIdRef.current = saved ? String(saved.id) : testId;
        if (saved) {
            setLevelId(String(saved.levelId));
            setType(saved.testType);
        }
        setTestsReloadKey((key) => key + 1);
        showNotice(`Test "${saved?.title ?? ""}" saved`);
    };

    const handleQuestionSaved = () => {
        setQuestionForm({ show: false, question: null });
        preferredTestIdRef.current = testId;
        setQuestionsReloadKey((key) => key + 1);
        setTestsReloadKey((key) => key + 1);
        showNotice("Question saved");
    };

    useEffect(() => {
        if (!levelId && levels.length > 0) setLevelId(String(levels[0].id));
    }, [levels, levelId]);

    useEffect(() => {
        if (!levelId) return;
        let cancelled = false;
        setLoadingTests(true);
        setError("");

        apiGet("/admin/content/tests", { levelId, type })
            .then((data) => {
                if (cancelled) return;
                const loaded = data || [];
                const preferred = preferredTestIdRef.current;
                preferredTestIdRef.current = null;

                setTests(loaded);
                setTestId(
                    preferred && loaded.some((t) => String(t.id) === preferred)
                        ? preferred
                        : loaded.length ? String(loaded[0].id) : ""
                );
            })
            .catch((err) => {
                if (cancelled) return;
                setTests([]);
                setTestId("");
                setError(err.message);
            })
            .finally(() => !cancelled && setLoadingTests(false));

        return () => {
            cancelled = true;
        };
    }, [levelId, type, testsReloadKey]);

    useEffect(() => {
        if (!testId) {
            setQuestions([]);
            return;
        }
        let cancelled = false;
        setLoadingQuestions(true);

        apiGet(`/admin/content/tests/${testId}/questions`)
            .then((data) => !cancelled && setQuestions(data || []))
            .catch((err) => {
                if (cancelled) return;
                setQuestions([]);
                setError(err.message);
            })
            .finally(() => !cancelled && setLoadingQuestions(false));

        return () => {
            cancelled = true;
        };
    }, [testId, questionsReloadKey]);

    const rows = useMemo(
        () => questions.map((question) => ({ question, issue: getQuestionIssue(question) })),
        [questions]
    );
    const issueCount = rows.filter((row) => row.issue).length;
    const visibleRows = showOnlyIssues ? rows.filter((row) => row.issue) : rows;
    const selectedTest = tests.find((test) => String(test.id) === testId);

    return (
        <>
            <Row className="g-2 mb-3">
                <Col md={4}>
                    <Form.Label className="small text-muted mb-1">Level</Form.Label>
                    <Form.Select value={levelId} onChange={(e) => setLevelId(e.target.value)}>
                        {levels.map((level) => (
                            <option key={level.id} value={level.id}>{level.title}</option>
                        ))}
                    </Form.Select>
                </Col>
                <Col md={4}>
                    <Form.Label className="small text-muted mb-1">Type</Form.Label>
                    <Form.Select value={type} onChange={(e) => setType(e.target.value)}>
                        {TEST_TYPES.map((t) => (
                            <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                    </Form.Select>
                </Col>
                <Col md={4}>
                    <Form.Label className="small text-muted mb-1">Unit</Form.Label>
                    <Form.Select
                        value={testId}
                        onChange={(e) => setTestId(e.target.value)}
                        disabled={loadingTests || tests.length === 0}
                    >
                        {tests.length === 0 && <option value="">No tests</option>}
                        {tests.map((test) => (
                            <option key={test.id} value={test.id}>
                                {test.screenOrder}. {test.title} ({test.questionCount})
                            </option>
                        ))}
                    </Form.Select>
                </Col>
            </Row>

            <div className="d-flex justify-content-end mb-3">
                <Button
                    variant="primary"
                    className="d-flex align-items-center gap-2"
                    onClick={() => setTestForm({ show: true, test: null })}
                    disabled={levels.length === 0}
                >
                    <PlusLg /> Add test
                </Button>
            </div>

            {notice && <Alert variant="success" className="py-2">{notice}</Alert>}
            {error && <Alert variant="danger" dismissible onClose={() => setError("")}>{error}</Alert>}

            {loadingTests || loadingQuestions ? (
                <div className="text-center py-4">
                    <Spinner animation="border" />
                </div>
            ) : !selectedTest ? (
                <p className="text-muted text-center py-4">No tests for this selection.</p>
            ) : (
                <>
                    <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                        <span className="fw-semibold">{selectedTest.title}</span>
                        <Button
                            size="sm"
                            variant="outline-secondary"
                            title="Edit test"
                            onClick={() => setTestForm({ show: true, test: selectedTest })}
                        >
                            <PencilSquare />
                        </Button>
                        {selectedTest.isPremium && <Badge bg="warning" text="dark">Premium</Badge>}
                        <Badge bg="secondary">{questions.length} questions</Badge>
                        {issueCount > 0 ? (
                            <Badge bg="danger">{issueCount} with problems</Badge>
                        ) : (
                            questions.length > 0 && <Badge bg="success">No problems found</Badge>
                        )}
                        <Form.Check
                            type="switch"
                            id="tests-only-issues"
                            className="ms-auto"
                            label="Show only problems"
                            checked={showOnlyIssues}
                            onChange={(e) => setShowOnlyIssues(e.target.checked)}
                            disabled={issueCount === 0}
                        />
                        <Button
                            size="sm"
                            variant="outline-primary"
                            className="d-flex align-items-center gap-1"
                            onClick={() => setQuestionForm({ show: true, question: null })}
                        >
                            <PlusLg /> Add question
                        </Button>
                    </div>

                    <Table bordered hover responsive className="align-middle">
                        <thead>
                            <tr>
                                <th style={{ width: 50 }}>#</th>
                                <th>Question</th>
                                {OPTION_KEYS.map((o) => (
                                    <th key={o.letter}>{o.label}</th>
                                ))}
                                <th style={{ width: 50 }} />
                            </tr>
                        </thead>
                        <tbody>
                            {visibleRows.map(({ question, issue }) => {
                                const correct = (question.correctOption || "").trim().toLowerCase();
                                return (
                                    <tr key={question.id} className={issue ? "table-warning" : ""}>
                                        <td>{question.screenOrder}</td>
                                        <td>
                                            {question.question}
                                            {issue && (
                                                <div className="small text-danger mt-1 d-flex align-items-center gap-1">
                                                    <ExclamationTriangleFill /> {issue}
                                                </div>
                                            )}
                                        </td>
                                        {OPTION_KEYS.map((o) => (
                                            <td
                                                key={o.letter}
                                                className={correct === o.letter ? "table-success fw-semibold" : ""}
                                            >
                                                {question[o.field]}
                                            </td>
                                        ))}
                                        <td>
                                            <Button
                                                size="sm"
                                                variant={issue ? "warning" : "outline-secondary"}
                                                title="Edit question"
                                                onClick={() => setQuestionForm({ show: true, question })}
                                            >
                                                <PencilSquare />
                                            </Button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                </>
            )}

            <TestFormModal
                show={testForm.show}
                test={testForm.test}
                defaults={testFormDefaults}
                levels={levels}
                onClose={() => setTestForm({ show: false, test: null })}
                onSaved={handleTestSaved}
            />

            <QuestionFormModal
                show={questionForm.show}
                testId={testId}
                question={questionForm.question}
                onClose={() => setQuestionForm({ show: false, question: null })}
                onSaved={handleQuestionSaved}
            />
        </>
    );
};

export default TestsTab;
